import { useProcessStartWizardWithCards } from '@frontend/utils/hooks/useProcessStartWizardWithCards';
import type { ICustomControlRequestReadProps } from '../../interfaces/customControlRequestReadProps';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { RequestStatus } from '../../../../../../common/enums/requestStatus';
import { ServiceCode } from '../../../../../../common/enums/serviceCode';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../translate';
import { CallBackMomentRead } from '../../../../elements/CallBackMomentRead';
import { getRequestReadButtons } from '../../../../elements/carePathButtons/RequestReadButtons';
import { CareProviderContactRead } from '../../../../elements/CareProviderContactRead';
import { ButtonContainer, UserTextParagraph } from '../../../../elements/common';
import { ContactWithRequesterOptionsRead } from '../../../../elements/ContactWithRequesterOptionsRead';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { FormSection } from '../../../../elements/forms/formSection/FormSection';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { PatientContactRead } from '../../../../elements/PatientContactRead';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { ReloadEpisode } from '../../../episodes/reloadEpisode';
import { RequestMessageContainer } from '../../../episodes/RequestMessageContainer';
import { ProfessionalSummaryDataRead } from '../../../professionalSummaryData/ProfessionalSummaryDataRead';
import { GgzcgV1FollowingSteps } from '../../GGZCg/v1/GgzcgV1FollowingSteps';
import { Ggzct1FollowingSteps } from '../../GGZCt/v1/GgzctV1FollowingSteps';
import { GgzctriageV1FollowingSteps } from '../../GGZCtriage/v1/GgzctriageV1FollowingSteps';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { MedicationsRead } from '../../utils/v1/medications/MedicationsRead';
import { ProfileImages } from '../../utils/v1/ProfileImages';
import { RequestExaminationThumbnails } from '../../utils/v1/RequestExaminationThumbnails';
import { SendRequestMessage } from '../../utils/v1/SendRequestMessage';
import { ServiceConsumerSupervisorRead } from '../../utils/v1/ServiceConsumerSupervisorRead';
import { TxcsMedicationsList } from '../../utils/v1/TxcsMedicationsList';
import { Ggzcf2fV1FollowingSteps } from './Ggzcf2fV1FollowingSteps';
import { QuestionRead } from './QuestionRead';

export function Ggzcf2fV1RequestRead(props: ICustomControlRequestReadProps) {
    const hasProcessStartWizardWithCards = useProcessStartWizardWithCards();

    return (
        <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
            <ProcessItemHeader
                date={props.process.dateSent || (props.request && props.request.dateSent)}
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) => {
                    await props.updateItemState({
                        ...props.itemState,
                        isOpen: isOpened || !!props.itemState.initialIsOpen,
                    });
                }}
                organizationName={props.process.requesterOrganization?.name}
                practitionerName={
                    props.process.requesterPractitioner
                        ? getDisplayName(props.process.requesterPractitioner)
                        : undefined
                }
                process={props.process}
                serviceType={props.process.serviceType}
                title={getServiceLabel(props.process.code, 'request', props.process.name)}
            />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                <FormSection isBorderLess={props.request.status !== RequestStatus.requested}>
                    <ProfileImages request={props.request} />
                    <ServiceConsumerSupervisorRead {...props} />
                </FormSection>

                <RequestExaminationThumbnails
                    headerText={translateKey('procedure.procedure')}
                    {...props}
                />
                <MedicationsRead
                    entity={ProcessEntity.request}
                    entityId={props.request.id}
                    patientUsesMedication={props.request.patientUsesMedication}
                />
                <ProfessionalSummaryDataRead request={props.request} />
                <TxcsMedicationsList requestId={props.request.id} />

                <QuestionRead {...props} />
                <PatientContactRead {...props} />
                <ContactWithRequesterOptionsRead {...props} />

                {props.request.customControlData?.beschikbaarheid && (
                    <CallBackMomentRead
                        callBackMoments={props.request.customControlData.beschikbaarheid}
                        requestPractitionerId={props.request.requesterPractitionerId}
                    />
                )}

                {props.request.customControlData.remarks && (
                    <HorizontalFormGroupRead>
                        <FormLabel>{translateKey('common.remarks')}</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>{props.request.customControlData.remarks}</UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                <CareProviderContactRead {...props} />

                {props.process.requestMessages && props.process.requestMessages.length > 0 && (
                    <RequestMessageContainer requestMessages={props.process.requestMessages} />
                )}

                {props.process.code === ServiceCode.GGZCf2f && (
                    <SendRequestMessage
                        isLastItem={props.isLastItem}
                        reloadAll={() => props.reloadProcess(ReloadEpisode.reloadAll)}
                        request={props.request}
                    />
                )}

                {props.process.code === ServiceCode.GGZCg && <GgzcgV1FollowingSteps />}
                {props.process.code === ServiceCode.GGZCf2f && <Ggzcf2fV1FollowingSteps />}
                {props.process.code === ServiceCode.GGZCt && <Ggzct1FollowingSteps />}
                {props.process.code === ServiceCode.GGZCtriage && <GgzctriageV1FollowingSteps />}

                <ButtonContainer
                    alignRight
                    buttons={[...getRequestReadButtons(props)]}
                    noMarginTop={!hasProcessStartWizardWithCards}
                />
            </CustomControlCollapseBody>

            {!props.itemState.initialIsOpen && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('request.hideConsult')}
                    labelMore={translateKey('request.showConsult')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}
        </ProcessContainer>
    );
}
